import { NavigateFunction } from 'react-router-dom';
import { Event, EventMetadataType } from '../models/event/event';
import { ROUTES } from '../constants/routes';
import { MINISTRY_MOCK_DATA_EN } from '../mock-data/ministry/ministry-mock-data';
import { SCHOOLS_MOCK_DATA_EN } from '../mock-data/school/schools-mock-data';

/**
 * Navigation utilities for handling different types of routing with 404 fallback
 */
export class NavigationUtil {
  /**
   * Handles event navigation based on event type and metadata with entity validation
   */
  static navigateToEvent(event: Event, navigate: NavigateFunction): void {
    // Check if it's a ministry recurring event
    if (event.metadata?.type === EventMetadataType.MINISTRY_RECURRING) {
      const ministryExists = NavigationUtil.validateMinistryExists(event.metadata.ministryId);
      if (!ministryExists) {
        navigate(ROUTES.NOT_FOUND);
        return;
      }
      navigate(`${ROUTES.MINISTRIES}/${event.metadata.ministryId}`);
      return;
    }
    
    // Validate event exists (basic check)
    if (!event.id || !event.key) {
      navigate(ROUTES.NOT_FOUND);
      return;
    }
    
    // Default to event detail page
    navigate(`${ROUTES.EVENTS}/${event.key}/${event.id}`);
  }

  /**
   * Gets the appropriate route for an event without navigating
   */
  static getEventRoute(event: Event): string {
    if (event.metadata?.type === EventMetadataType.MINISTRY_RECURRING) {
      const ministryExists = NavigationUtil.validateMinistryExists(event.metadata.ministryId);
      if (!ministryExists) {
        return ROUTES.NOT_FOUND;
      }
      return `${ROUTES.MINISTRIES}/${event.metadata.ministryId}`;
    }
    
    // Validate event exists (basic check)
    if (!event.id || !event.key) {
      return ROUTES.NOT_FOUND;
    }
    
    return `${ROUTES.EVENTS}/${event.key}/${event.id}`;
  }

  /**
   * Validates if a ministry exists by ID
   */
  static validateMinistryExists(ministryId?: string): boolean {
    if (!ministryId) return false;
    const id = parseInt(ministryId);
    return MINISTRY_MOCK_DATA_EN.some(ministry => ministry.id === id);
  }

  /**
   * Validates if a school exists by ID
   */
  static validateSchoolExists(schoolId?: string): boolean {
    if (!schoolId) return false;
    const id = parseInt(schoolId);
    return SCHOOLS_MOCK_DATA_EN.some(school => school.id === id);
  }

  /**
   * Safe navigation to ministry with validation
   */
  static navigateToMinistry(ministryId: string, navigate: NavigateFunction): void {
    if (!NavigationUtil.validateMinistryExists(ministryId)) {
      navigate(ROUTES.NOT_FOUND);
      return;
    }
    navigate(`${ROUTES.MINISTRIES}/${ministryId}`);
  }

  /**
   * Safe navigation to school with validation
   */
  static navigateToSchool(schoolId: string, navigate: NavigateFunction): void {
    if (!NavigationUtil.validateSchoolExists(schoolId)) {
      navigate(ROUTES.NOT_FOUND);
      return;
    }
    navigate(`${ROUTES.SCHOOLS}/${schoolId}`);
  }

  /**
   * General safe navigation with entity validation
   */
  static navigateWithValidation(
    entityType: 'ministry' | 'school' | 'event', 
    entityId: string, 
    navigate: NavigateFunction,
    eventKey?: string
  ): void {
    switch (entityType) {
      case 'ministry':
        NavigationUtil.navigateToMinistry(entityId, navigate);
        break;
      case 'school':
        NavigationUtil.navigateToSchool(entityId, navigate);
        break;
      case 'event':
        if (!eventKey) {
          navigate(ROUTES.NOT_FOUND);
          return;
        }
        // Basic validation - could be enhanced with event service lookup
        navigate(`${ROUTES.EVENTS}/${eventKey}/${entityId}`);
        break;
      default:
        navigate(ROUTES.NOT_FOUND);
    }
  }
}